import { useState, useEffect, useCallback, useRef, Component } from 'react'
import { useOutletContext, NavLink } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.jsx'
import PlotlyChart from './PlotlyChart.jsx'
import ChatBar from './ChatBar.jsx'
import styles from './Dashboard.module.css'

const DASHBOARD_PROMPTS = [
  'What drove the change in my key metrics this week?',
  'Which metrics are trending in the wrong direction?',
  'Show me a breakdown by category or region',
  'How does this week compare to last week?',
  'What should I be paying attention to right now?',
]

// ── Chart error boundary ───────────────────────────────────────────────────────

class ChartErrorBoundary extends Component {
  state = { error: null }
  static getDerivedStateFromError(e) { return { error: e } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%',
          color:'rgba(239,68,68,0.7)', fontSize:'12px', fontFamily:'IBM Plex Mono, monospace' }}>
          Chart failed to render
        </div>
      )
    }
    return this.props.children
  }
}

// ── Expand modal ───────────────────────────────────────────────────────────────

function ExpandModal({ widget, onClose }) {
  const isChart = widget.chart_type === 'plotly' && widget.chart_spec

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className={styles.expandOverlay} onMouseDown={onClose}>
      <div className={styles.expandModal} onMouseDown={e => e.stopPropagation()}>
        <div className={styles.expandHeader}>
          <span className={styles.expandTitle}>{widget.title}</span>
          <button className={styles.expandClose} onClick={onClose}>✕</button>
        </div>
        <div className={styles.expandBody}>
          {isChart ? (
            <ChartErrorBoundary>
              <PlotlyChart
                spec={widget.chart_spec}
                config={{ displayModeBar: true, modeBarButtonsToRemove: ['sendDataToCloud'], responsive: true }}
                style={{ width: '100%', height: '100%', minHeight: 'unset' }}
              />
            </ChartErrorBoundary>
          ) : (
            <div
              className={styles.expandText}
              dangerouslySetInnerHTML={{ __html: widget.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Move-to dropdown ───────────────────────────────────────────────────────────

function MoveMenu({ sections, currentSectionId, onMove }) {
  const ref = useRef(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className={styles.moveWrap} ref={ref}>
      <button className={styles.moveBtn} onClick={() => setOpen(o => !o)}>Move →</button>
      {open && (
        <div className={styles.moveMenu}>
          {sections.filter(s => s.id !== currentSectionId).map(s => (
            <button key={s.id} className={styles.moveMenuItem} onClick={() => { onMove(s.id); setOpen(false) }}>
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Pinned insight card ────────────────────────────────────────────────────────

function PinnedWidget({ widget, editMode, sections, onRemove, onMove, onExpand,
                        dragging, dragOver, onDragStart, onDragOver, onDrop, onDragEnd,
                        chartRefreshError, isRepairing, onRepairChart, isRegenerating, onRegenerateChart,
                        isSyncing, onSyncModel }) {
  const [expanded, setExpanded] = useState(false)
  const isChart = widget.chart_type === 'plotly' && widget.chart_spec
  const isLegacy = widget.chart_type === 'plotly' && !widget.sql && !widget.chart_recipe
  const preview = widget.content.length > 200 ? widget.content.slice(0, 200) + '…' : widget.content
  const d = new Date(widget.refreshed_at || widget.created_at)
  const timeStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  const isStale = isChart && (widget.refresh_status === 'error' || !!chartRefreshError)
  const staleDateStr = new Date(widget.refreshed_at || widget.created_at)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return (
    <div
      className={[
        styles.pinnedCard,
        dragging  ? styles.dragging  : '',
        dragOver  ? styles.dragOver  : '',
        editMode  ? styles.draggable : '',
      ].join(' ')}
      draggable={editMode}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <div className={styles.pinnedHeader}>
        <span className={styles.pinnedIcon}>◈</span>
        <span className={styles.pinnedTitle}>{widget.title}</span>
        <span className={styles.pinnedTime}>{timeStr}</span>
        <button className={styles.expandBtn} onClick={() => onExpand(widget)} title="Expand">⤢</button>
        {editMode && (
          <>
            <MoveMenu sections={sections} currentSectionId={widget.section_id || 'overview'} onMove={(sid) => onMove(widget.widget_id, sid)} />
            <button className={styles.removeBtn} onClick={() => onRemove(widget.widget_id)} title="Remove">✕</button>
          </>
        )}
      </div>
      {isChart ? (
        <>
          <div className={styles.pinnedChartContainer}>
            <ChartErrorBoundary>
              <PlotlyChart spec={widget.chart_spec} />
            </ChartErrorBoundary>
          </div>
          {!isStale && (
            <div className={styles.pinnedChartFooter}>
              {widget.model_name
                ? <span className={styles.modelBadge} title={`dbt model: ${widget.model_name}`}>{widget.model_name}</span>
                : !isLegacy && (
                  <button
                    className={styles.syncModelBtn}
                    disabled={isSyncing}
                    onClick={() => onSyncModel(widget.widget_id)}
                    title="Create a rpt_ dbt model from this chart's SQL"
                  >
                    {isSyncing ? 'Syncing…' : '↑ Sync to Lake'}
                  </button>
                )
              }
              <span>updated {timeStr}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <div
            className={styles.pinnedContent}
            dangerouslySetInnerHTML={{ __html: (expanded ? widget.content : preview).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
          />
          {widget.content.length > 200 && (
            <button className={styles.pinnedExpand} onClick={() => setExpanded(e => !e)}>
              {expanded ? '▲ Show less' : '▼ Show more'}
            </button>
          )}
        </>
      )}
      {isLegacy && (
        <div className={styles.staleRow}>
          <span className={styles.chartStale} title={chartRefreshError || widget.refresh_error || ''}>
            {chartRefreshError || widget.refresh_error
              ? `⚠ regenerate failed — ${chartRefreshError || widget.refresh_error}`
              : '⚠ chart data is outdated — regenerate to use current columns'}
          </span>
          <button
            className={styles.fixAiBtn}
            disabled={isRegenerating}
            onClick={() => onRegenerateChart(widget.widget_id)}
          >
            {isRegenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      )}
      {isStale && !isLegacy && (
        <div className={styles.staleRow}>
          <span className={styles.chartStale} title={chartRefreshError || widget.refresh_error || ''}>
            ⚠ live refresh unavailable — showing snapshot from {staleDateStr}
          </span>
          <button
            className={styles.fixAiBtn}
            disabled={isRepairing}
            onClick={() => onRepairChart(widget.widget_id)}
          >
            {isRepairing ? 'Fixing…' : 'Fix with AI'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Metric card ────────────────────────────────────────────────────────────────

function MetricCard({ m, editMode, sections, metricsRefreshing, metricErrors, metricRepairing,
                      confirmDeleteMetric, deletingMetric, onDelete, onConfirmDelete, onCancelDelete,
                      onMoveMetric, onRepairMetric, onCalculate }) {
  const [defOpen, setDefOpen] = useState(false)
  const refreshError = metricErrors.get(m.name)
  const isStale      = m.refresh_status === 'error' || !!refreshError
  const isRepairing  = metricRepairing.has(m.name)
  const isDeleting   = deletingMetric.has(m.name)
  const confirming   = confirmDeleteMetric === m.name
  const hasDefinition = m.description || m.calculation

  return (
    <div className={styles.metricCard}>
      <div className={styles.metricTop}>
        <span className={styles.metricCardLabel}>{m.label}</span>
        {m.grain && m.grain !== 'row' && <span className={styles.metricGrain}>{m.grain}</span>}
        {editMode && (
          <div className={styles.metricActions}>
            <MoveMenu
              sections={sections}
              currentSectionId={m.section_id || 'overview'}
              onMove={(sid) => onMoveMetric(m.name, sid)}
            />
            {confirming ? (
              <>
                <button className={styles.confirmDeleteBtn} disabled={isDeleting} onClick={() => onDelete(m.name)}>
                  {isDeleting ? '…' : 'Confirm'}
                </button>
                <button className={styles.cancelDeleteBtn} onClick={onCancelDelete}>Cancel</button>
              </>
            ) : (
              <button className={styles.metricRemoveBtn} onClick={() => onConfirmDelete(m.name)}>✕</button>
            )}
          </div>
        )}
      </div>
      <div className={styles.metricValue}>
        {m.current_value != null
          ? <>
              {m.unit === 'USD' || m.unit === '$'
                ? `$${Number(m.current_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                : m.unit === '%'
                  ? `${Number(m.current_value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
                  : `${Number(m.current_value).toLocaleString()}${m.unit ? ` ${m.unit}` : ''}`
              }
            </>
          : <span className={styles.metricValueEmpty}>{metricsRefreshing ? '…' : '—'}</span>
        }
      </div>
      <div className={styles.metricMeta}>
        <span className={styles.metricModel}>{m.model}</span>
        {m.value_computed_at && <span className={styles.metricGrain}>as of {m.value_computed_at}</span>}
        {m.current_value == null && !metricsRefreshing && (
          <button className={styles.metricCalcBtn} onClick={() => onCalculate(m.label)}>
            Calculate →
          </button>
        )}
      </div>
      {isStale && (
        <div className={styles.staleRow}>
          <span className={styles.metricStale} title={refreshError || m.refresh_error || ''}>
            ⚠ refresh failed — showing data as of {m.value_computed_at || 'unknown'}
          </span>
          <button className={styles.fixAiBtn} disabled={isRepairing} onClick={() => onRepairMetric(m.name)}>
            {isRepairing ? 'Fixing…' : 'Fix with AI'}
          </button>
        </div>
      )}
      {hasDefinition && (
        <>
          <button className={styles.metricDefToggle} onClick={() => setDefOpen(o => !o)}>
            {defOpen ? '▲ Hide definition' : '▼ Definition'}
          </button>
          {defOpen && (
            <div className={styles.metricDef}>
              {m.description && <p className={styles.metricDefDesc}>{m.description}</p>}
              {m.calculation && <code className={styles.metricDefSql}>{m.calculation}</code>}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Section tab ────────────────────────────────────────────────────────────────

function SectionTab({ section, active, editMode, onClick, onRename, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState(section.name)
  const inputRef = useRef(null)

  const commit = () => {
    setEditing(false)
    if (draft.trim() && draft.trim() !== section.name) onRename(section.id, draft.trim())
    else setDraft(section.name)
  }

  useEffect(() => { if (editing) inputRef.current?.select() }, [editing])

  return (
    <div className={`${styles.sectionTab} ${active ? styles.sectionTabActive : ''}`}>
      {editing ? (
        <input
          ref={inputRef}
          className={styles.sectionTabInput}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(section.name) } }}
        />
      ) : (
        <button
          className={styles.sectionTabBtn}
          onClick={onClick}
          onDoubleClick={() => { if (editMode && section.id !== 'overview') setEditing(true) }}
        >
          {section.name}
        </button>
      )}
      {editMode && section.id !== 'overview' && !editing && (
        <button className={styles.sectionTabDel} onClick={() => onDelete(section.id)} title="Delete section">✕</button>
      )}
    </div>
  )
}

// ── Dashboard ──────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { tenantProfile, dataRefreshKey, helpPrompt, consumeHelpPrompt } = useOutletContext()
  const { apiFetch, user, selectedTenantId } = useAuth()
  const [metrics, setMetrics]             = useState([])
  const [metricsRefreshing, setMetricsRefreshing] = useState(false)
  const [metricErrors, setMetricErrors]   = useState(new Map())
  const [metricRepairing, setMetricRepairing] = useState(new Set())
  const [confirmDeleteMetric, setConfirmDeleteMetric] = useState(null)
  const [deletingMetric, setDeletingMetric] = useState(new Set())
  const [chartRegenerating, setChartRegenerating] = useState(new Set())
  const [chartSyncing, setChartSyncing] = useState(new Set())
  const [pinnedWidgets, setPinnedWidgets] = useState([])
  const [chartRefreshErrors, setChartRefreshErrors] = useState(new Map())
  const [chartRepairing, setChartRepairing] = useState(new Set())
  const [sections, setSections]           = useState([{ id: 'overview', name: 'Overview', order: 0 }])
  const [activeSection, setActiveSection] = useState('overview')
  const [editMode, setEditMode]           = useState(false)
  const [addingSection, setAddingSection] = useState(false)
  const [newSectionName, setNewSectionName] = useState('')
  const [expandedWidget, setExpandedWidget] = useState(null)
  const [dragId, setDragId]               = useState(null)
  const [dragOverId, setDragOverId]       = useState(null)
  const newSectionInputRef = useRef(null)
  const chatRef = useRef(null)

  // Recompute KPI metrics against live Athena data; merges results into `metrics`
  // by name and tracks per-metric refresh errors without persisting them.
  const refreshMetrics = useCallback(async () => {
    setMetricsRefreshing(true)
    try {
      const res = await apiFetch('/api/lake/metrics/refresh', { method: 'POST', body: JSON.stringify({}) })
      if (res.ok) {
        const d = await res.json()
        const fresh = d.metrics ?? []
        const byName = new Map(fresh.map(m => [m.name, m]))
        setMetrics(prev => prev.map(m => byName.get(m.name) ?? m))
        setMetricErrors(() => {
          const next = new Map()
          for (const m of fresh) {
            if (m.refresh_error) next.set(m.name, m.refresh_error)
          }
          return next
        })
      }
    } catch {
      // best-effort — keep showing last-known metric values on failure
    } finally {
      setMetricsRefreshing(false)
    }
  }, [apiFetch])

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await apiFetch('/api/lake/metrics')
      if (res.ok) {
        const d = await res.json()
        setMetrics(d.metrics ?? [])
        refreshMetrics()
      }
    } catch {
      // best-effort — leave metrics empty on failure
    }
  }, [apiFetch, refreshMetrics])

  const REFRESH_CONCURRENCY = 4

  // Re-run live SQL for pinned charts that captured a recipe (sql + create_chart args).
  // Legacy widgets without sql/chart_recipe are skipped — they keep their static snapshot.
  const refreshChartWidgets = useCallback(async (widgets) => {
    const refreshable = widgets.filter(w => w.sql && w.chart_recipe)
    for (let i = 0; i < refreshable.length; i += REFRESH_CONCURRENCY) {
      const batch = refreshable.slice(i, i + REFRESH_CONCURRENCY)
      const results = await Promise.allSettled(batch.map(async w => {
        const res = await apiFetch(`/api/dashboard/${w.widget_id}/refresh`, { method: 'POST', body: JSON.stringify({}) })
        const data = await res.json().catch(() => ({}))
        return { widgetId: w.widget_id, data }
      }))
      for (const r of results) {
        if (r.status !== 'fulfilled') continue
        const { widgetId, data } = r.value
        if (data.refreshed && data.chart_spec) {
          setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId
            ? { ...w, chart_spec: data.chart_spec, refresh_status: 'ok', ...(data.refreshed_at ? { refreshed_at: data.refreshed_at } : {}) }
            : w))
          setChartRefreshErrors(prev => {
            if (!prev.has(widgetId)) return prev
            const next = new Map(prev)
            next.delete(widgetId)
            return next
          })
        } else if (data.error) {
          setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId
            ? { ...w, refresh_status: 'error' }
            : w))
          setChartRefreshErrors(prev => new Map(prev).set(widgetId, data.error))
        }
      }
    }
  }, [apiFetch])

  const fetchPinned = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboard')
      if (res.ok) {
        const d = await res.json()
        const widgets = d.widgets ?? []
        setPinnedWidgets(widgets)
        refreshChartWidgets(widgets)
      }
    } catch {
      // best-effort — leave pinned widgets empty on failure
    }
  }, [apiFetch, refreshChartWidgets])

  const fetchSections = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboard/sections')
      if (res.ok) { const d = await res.json(); setSections(d.sections ?? []) }
    } catch {
      // best-effort — keep the default "Overview" section on failure
    }
  }, [apiFetch])

  // "Fix with AI" — ask the AI to propose a corrected model/calculation for a
  // broken metric, then re-pull the metric list (and re-run a live refresh).
  const repairMetric = useCallback(async (name) => {
    setMetricRepairing(prev => new Set(prev).add(name))
    try {
      await apiFetch(`/api/lake/metrics/${encodeURIComponent(name)}/repair`, { method: 'POST', body: JSON.stringify({}) })
    } catch {
      // best-effort — fetchMetrics below reflects whatever state resulted
    } finally {
      setMetricRepairing(prev => { const next = new Set(prev); next.delete(name); return next })
      fetchMetrics()
    }
  }, [apiFetch, fetchMetrics])

  const deleteMetric = useCallback(async (name) => {
    setDeletingMetric(prev => new Set(prev).add(name))
    setConfirmDeleteMetric(null)
    try {
      const res = await apiFetch(`/api/lake/metrics/${encodeURIComponent(name)}`, { method: 'DELETE' })
      if (res.ok) setMetrics(prev => prev.filter(m => m.name !== name))
    } catch {
      // best-effort — re-fetch on failure so UI stays consistent
      fetchMetrics()
    } finally {
      setDeletingMetric(prev => { const next = new Set(prev); next.delete(name); return next })
    }
  }, [apiFetch, fetchMetrics])

  const moveMetric = useCallback(async (name, sectionId) => {
    setMetrics(prev => prev.map(m => m.name === name ? { ...m, section_id: sectionId } : m))
    try {
      await apiFetch(`/api/lake/metrics/${encodeURIComponent(name)}`, {
        method: 'PATCH',
        body: JSON.stringify({ section_id: sectionId }),
      })
    } catch {
      // best-effort — optimistic update already applied
    }
  }, [apiFetch])

  // "Fix with AI" — ask the AI to propose a corrected sql/recipe for a broken
  // chart widget, then merge the returned chart_spec back into pinnedWidgets.
  const repairChartWidget = useCallback(async (widgetId) => {
    setChartRepairing(prev => new Set(prev).add(widgetId))
    try {
      const res = await apiFetch(`/api/dashboard/${widgetId}/repair`, { method: 'POST', body: JSON.stringify({}) })
      const data = await res.json().catch(() => ({}))
      if (data.repaired && data.chart_spec) {
        setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId
          ? { ...w, chart_spec: data.chart_spec, sql: data.sql ?? w.sql, chart_recipe: data.chart_recipe ?? w.chart_recipe,
              source_models: data.source_models ?? w.source_models, refresh_status: 'ok',
              ...(data.refreshed_at ? { refreshed_at: data.refreshed_at } : {}) }
          : w))
        setChartRefreshErrors(prev => {
          if (!prev.has(widgetId)) return prev
          const next = new Map(prev)
          next.delete(widgetId)
          return next
        })
      } else if (data.error) {
        setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId ? { ...w, refresh_status: 'error' } : w))
        setChartRefreshErrors(prev => new Map(prev).set(widgetId, data.error))
      }
    } catch {
      // best-effort — leave widget in its current state on transport failure
    } finally {
      setChartRepairing(prev => { const next = new Set(prev); next.delete(widgetId); return next })
    }
  }, [apiFetch])

  const regenerateChartWidget = useCallback(async (widgetId) => {
    setChartRegenerating(prev => new Set(prev).add(widgetId))
    try {
      const res = await apiFetch(`/api/dashboard/${widgetId}/regenerate`, { method: 'POST', body: JSON.stringify({}) })
      const data = await res.json().catch(() => ({}))
      if (data.regenerated && data.chart_spec) {
        setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId
          ? { ...w, chart_spec: data.chart_spec, sql: data.sql, chart_recipe: data.chart_recipe,
              source_models: data.source_models ?? w.source_models, refresh_status: 'ok',
              ...(data.refreshed_at ? { refreshed_at: data.refreshed_at } : {}) }
          : w))
      } else if (data.error) {
        setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId ? { ...w, refresh_status: 'error' } : w))
        setChartRefreshErrors(prev => new Map(prev).set(widgetId, data.error))
      }
    } catch {
      // best-effort — leave widget in its current state on transport failure
    } finally {
      setChartRegenerating(prev => { const next = new Set(prev); next.delete(widgetId); return next })
    }
  }, [apiFetch])

  const syncModelWidget = useCallback(async (widgetId) => {
    setChartSyncing(prev => new Set(prev).add(widgetId))
    try {
      const res = await apiFetch(`/api/dashboard/${widgetId}/sync-model`, { method: 'POST', body: JSON.stringify({}) })
      const data = await res.json().catch(() => ({}))
      if (data.synced && data.model_name) {
        setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId ? { ...w, model_name: data.model_name } : w))
      }
    } catch {
      // best-effort
    } finally {
      setChartSyncing(prev => { const next = new Set(prev); next.delete(widgetId); return next })
    }
  }, [apiFetch])

  useEffect(() => { fetchMetrics(); fetchPinned(); fetchSections() }, [fetchMetrics, fetchPinned, fetchSections])
  useEffect(() => { if (dataRefreshKey) fetchPinned() }, [dataRefreshKey, fetchPinned])
  useEffect(() => { if (addingSection) newSectionInputRef.current?.focus() }, [addingSection])
  useEffect(() => {
    if (!helpPrompt) return
    chatRef.current?.setInput(helpPrompt)
    consumeHelpPrompt()
  }, [helpPrompt, consumeHelpPrompt])

  // ── Print current section ────────────────────────────────────────────────────

  const handlePrint = useCallback(() => {
    const style = document.createElement('style')
    style.textContent = `
      @media print {
        aside, nav { display: none !important; }
        [class*="chatSide"] { display: none !important; }
        [class*="sectionTabs"] { display: none !important; }
        [class*="headerActions"] { display: none !important; }
        [class*="moveWrap"], [class*="removeBtn"], [class*="expandBtn"] { display: none !important; }
        [class*="dashRoot"] { display: block !important; overflow: visible !important; height: auto !important; }
        [class*="dashContent"] { overflow: visible !important; height: auto !important; padding: 16px !important; }
        [class*="pinnedCard"] { break-inside: avoid; page-break-inside: avoid; }
        [class*="pinnedGrid"] { display: grid !important; grid-template-columns: 1fr 1fr; gap: 16px; }
        @page { margin: 1.5cm; }
      }
    `
    document.head.appendChild(style)
    const cleanup = () => { document.head.removeChild(style); window.removeEventListener('afterprint', cleanup) }
    window.addEventListener('afterprint', cleanup)
    window.print()
  }, [])

  // ── Widget actions ───────────────────────────────────────────────────────────

  const removePinned = useCallback(async (widgetId) => {
    setPinnedWidgets(prev => prev.filter(w => w.widget_id !== widgetId))
    try { await apiFetch(`/api/dashboard/${widgetId}`, { method: 'DELETE' }) } catch { /* local state already updated optimistically */ }
  }, [apiFetch])

  const moveWidget = useCallback(async (widgetId, sectionId) => {
    setPinnedWidgets(prev => prev.map(w => w.widget_id === widgetId ? { ...w, section_id: sectionId } : w))
    try {
      await apiFetch(`/api/dashboard/widget/${widgetId}/section`, {
        method: 'PATCH',
        body: JSON.stringify({ section_id: sectionId }),
      })
    } catch {
      // local state already updated optimistically
    }
  }, [apiFetch])

  // ── Drag-to-reorder ──────────────────────────────────────────────────────────

  const handleDragStart = useCallback((widgetId) => {
    setDragId(widgetId)
  }, [])

  const handleDragOver = useCallback((e, widgetId) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverId(widgetId)
  }, [])

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault()
    setDragId(null)
    setDragOverId(null)
    if (!dragId || dragId === targetId) return

    setPinnedWidgets(prev => {
      const visible = prev
        .filter(w => (w.section_id || 'overview') === activeSection)
        .sort((a, b) => (a.position ?? 999) - (b.position ?? 999) || (a.created_at > b.created_at ? 1 : -1))

      const srcIdx = visible.findIndex(w => w.widget_id === dragId)
      const tgtIdx = visible.findIndex(w => w.widget_id === targetId)
      if (srcIdx === -1 || tgtIdx === -1) return prev

      const reordered = [...visible]
      const [item] = reordered.splice(srcIdx, 1)
      reordered.splice(tgtIdx, 0, item)

      const posMap = Object.fromEntries(reordered.map((w, i) => [w.widget_id, i]))
      const next = prev.map(w => posMap[w.widget_id] !== undefined ? { ...w, position: posMap[w.widget_id] } : w)

      // Persist in background
      apiFetch('/api/dashboard/reorder', {
        method: 'PATCH',
        body: JSON.stringify({ updates: reordered.map((w, i) => ({ widget_id: w.widget_id, position: i })) }),
      }).catch(() => {})

      return next
    })
  }, [dragId, activeSection, apiFetch])

  const handleDragEnd = useCallback(() => {
    setDragId(null)
    setDragOverId(null)
  }, [])

  // ── Section management ───────────────────────────────────────────────────────

  const createSection = useCallback(async () => {
    const name = newSectionName.trim()
    if (!name) { setAddingSection(false); return }
    try {
      const res = await apiFetch('/api/dashboard/sections', { method: 'POST', body: JSON.stringify({ name }) })
      if (res.ok) {
        const s = await res.json()
        setSections(prev => [...prev, s])
        setActiveSection(s.id)
      }
    } catch {
      // best-effort — section won't appear, form still resets below
    }
    setAddingSection(false)
    setNewSectionName('')
  }, [apiFetch, newSectionName])

  const renameSection = useCallback(async (sectionId, name) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, name } : s))
    try {
      await apiFetch(`/api/dashboard/sections/${sectionId}`, { method: 'PATCH', body: JSON.stringify({ name }) })
    } catch {
      // local state already updated optimistically
    }
  }, [apiFetch])

  const deleteSection = useCallback(async (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId))
    setPinnedWidgets(prev => prev.map(w => w.section_id === sectionId ? { ...w, section_id: 'overview' } : w))
    if (activeSection === sectionId) setActiveSection('overview')
    try { await apiFetch(`/api/dashboard/sections/${sectionId}`, { method: 'DELETE' }) } catch { /* local state already updated optimistically */ }
  }, [apiFetch, activeSection])

  // ── Render ───────────────────────────────────────────────────────────────────

  const now = new Date()
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening'
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })

  const visibleWidgets = pinnedWidgets
    .filter(w => (w.section_id || 'overview') === activeSection)
    .sort((a, b) => (a.position ?? 999) - (b.position ?? 999) || (a.created_at > b.created_at ? 1 : -1))

  const hasAnyWidgets = pinnedWidgets.length > 0

  return (
    <div className={styles.dashRoot}>

      {expandedWidget && (
        <ExpandModal widget={expandedWidget} onClose={() => setExpandedWidget(null)} />
      )}

      {/* Left: Dashboard content */}
      <div className={`${styles.dashContent} anim-fade`}>

        <div className={styles.header}>
          <div>
            <div className={styles.greeting}>{greeting}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</div>
            <div className={styles.subGreeting}>
              <span className={styles.org}>{tenantProfile?.company_name ?? 'Your Organization'}</span>
              <span className={styles.dot}>·</span>
              <span className={styles.dateStr}>{dateStr}</span>
            </div>
          </div>
          <div className={styles.headerActions}>
            {visibleWidgets.length > 0 && (
              <button className={styles.printBtn} onClick={handlePrint}>⎙ Print</button>
            )}
            {hasAnyWidgets && (
              <button
                className={`${styles.editToggle} ${editMode ? styles.editToggleOn : ''}`}
                onClick={() => { setEditMode(e => !e); setAddingSection(false) }}
              >
                {editMode ? '✓ Done' : '✎ Edit'}
              </button>
            )}
          </div>
        </div>

        {/* Section tabs */}
        <div className={styles.sectionTabs}>
          {sections.map(s => (
            <SectionTab
              key={s.id}
              section={s}
              active={activeSection === s.id}
              editMode={editMode}
              onClick={() => setActiveSection(s.id)}
              onRename={renameSection}
              onDelete={deleteSection}
            />
          ))}
          {editMode && !addingSection && (
            <button className={styles.addSectionBtn} onClick={() => { setAddingSection(true); setNewSectionName('') }}>
              + Section
            </button>
          )}
          {addingSection && (
            <input
              ref={newSectionInputRef}
              className={styles.newSectionInput}
              placeholder="Section name…"
              value={newSectionName}
              onChange={e => setNewSectionName(e.target.value)}
              onBlur={createSection}
              onKeyDown={e => { if (e.key === 'Enter') createSection(); if (e.key === 'Escape') { setAddingSection(false); setNewSectionName('') } }}
            />
          )}
        </div>

        {metrics.filter(m => (m.section_id || 'overview') === activeSection).length > 0 && (
          <section className={styles.section}>
            <div className={styles.sectionRow}>
              <div className={styles.sectionLabel}>Metrics</div>
              {metricsRefreshing && <span className={styles.metricsRefreshing}>Metrics · refreshing…</span>}
            </div>
            <div className={styles.metricGrid}>
              {metrics.filter(m => (m.section_id || 'overview') === activeSection).map(m => (
                <MetricCard
                  key={m.name}
                  m={m}
                  editMode={editMode}
                  sections={sections}
                  metricsRefreshing={metricsRefreshing}
                  metricErrors={metricErrors}
                  metricRepairing={metricRepairing}
                  confirmDeleteMetric={confirmDeleteMetric}
                  deletingMetric={deletingMetric}
                  onDelete={deleteMetric}
                  onConfirmDelete={setConfirmDeleteMetric}
                  onCancelDelete={() => setConfirmDeleteMetric(null)}
                  onMoveMetric={moveMetric}
                  onRepairMetric={repairMetric}
                  onCalculate={(label) => chatRef.current?.setInput(`Calculate the current value for the "${label}" KPI`)}
                />
              ))}
            </div>
          </section>
        )}

        <section className={styles.section}>
          {visibleWidgets.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>◈</div>
              <div className={styles.emptyStateTitle}>No pinned insights yet</div>
              <div className={styles.emptyStateSub}>
                Ask a question in the chat panel and pin the response to track it here.
              </div>
            </div>
          ) : (
            <div className={styles.pinnedGrid}>
              {visibleWidgets.map(w => (
                <PinnedWidget
                  key={w.widget_id}
                  widget={w}
                  editMode={editMode}
                  sections={sections}
                  onRemove={removePinned}
                  onMove={moveWidget}
                  onExpand={setExpandedWidget}
                  dragging={dragId === w.widget_id}
                  dragOver={dragOverId === w.widget_id}
                  onDragStart={() => handleDragStart(w.widget_id)}
                  onDragOver={(e) => handleDragOver(e, w.widget_id)}
                  onDrop={(e) => handleDrop(e, w.widget_id)}
                  onDragEnd={handleDragEnd}
                  chartRefreshError={chartRefreshErrors.get(w.widget_id)}
                  isRepairing={chartRepairing.has(w.widget_id)}
                  onRepairChart={repairChartWidget}
                  isRegenerating={chartRegenerating.has(w.widget_id)}
                  onRegenerateChart={regenerateChartWidget}
                  isSyncing={chartSyncing.has(w.widget_id)}
                  onSyncModel={syncModelWidget}
                />
              ))}
            </div>
          )}
        </section>

      </div>

      {/* Right: Chat panel */}
      <aside className={styles.chatSide}>
        <div className={styles.chatSideHeader}>
          <span className={styles.chatSideTitle}>Ask AI</span>
          <NavLink to="/analytics" className={styles.chatSideLink}>
            All conversations →
          </NavLink>
        </div>
        <ChatBar
          ref={chatRef}
          key={`${user?.sub}-${selectedTenantId || 'own'}`}
          userId={user?.sub}
          panelMode
          pageContext="dashboard"
          suggestedPrompts={DASHBOARD_PROMPTS}
          onDataChanged={fetchPinned}
        />
      </aside>

    </div>
  )
}
